'use client'

import React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@segway/core'

import { Close } from '@/components'

const UIDialog = DialogPrimitive.Root

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'bg-background fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        className,
      )}
      {...props}>
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

type PointerDownOutsideEvent = CustomEvent<{
  originalEvent: PointerEvent
}>

type FocusOutsideEvent = CustomEvent<{
  originalEvent: FocusEvent
}>

interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hasCloseIcon?: boolean
  closeIcon?: React.ReactNode
  closeIconContainerStyle?: string
  containerStyle?: string
  title?: string
  titleStyle?: string
  description?: string
  descriptionStyle?: string
  children: React.ReactNode
  footer?: React.ReactNode
  ariaTitle?: string
  isMaskCloseble?: boolean
}

/**
 * Dialog 组件
 */
const Dialog = ({
  open,
  onOpenChange,
  hasCloseIcon = true,
  closeIconContainerStyle,
  containerStyle,
  title,
  titleStyle,
  description,
  descriptionStyle,
  children,
  footer,
  ariaTitle,
  isMaskCloseble = true,
}: DialogProps) => {
  return (
    <UIDialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(event: PointerDownOutsideEvent | FocusOutsideEvent) => {
          if (isMaskCloseble) {
            event.preventDefault()
          }
        }}
        className={cn('w-[600px] max-w-full bg-white p-7', containerStyle)}>
        {hasCloseIcon && (
          <DialogClose
            className={cn('absolute right-7 top-7 z-10 outline-none', closeIconContainerStyle)}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-2">
              <Close rotate={45} />
            </div>
          </DialogClose>
        )}
        <DialogHeader>
          {title ? (
            <DialogTitle
              className={cn(
                'font-miSansDemiBold450 text-4xl leading-[1.4] text-gray-8',
                titleStyle,
              )}
              style={{ fontWeight: 'unset' }}>
              {title}
            </DialogTitle>
          ) : (
            <DialogTitle className="sr-only">{ariaTitle || 'Dialog'}</DialogTitle>
          )}
          {description && (
            <DialogDescription
              className={cn('font-miSansMedium380 text-lg text-gray-5', descriptionStyle)}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </UIDialog>
  )
}

export default Dialog
